import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { Check, FileUp, Info, Plus, Trash2 } from 'lucide-react';
import { toast } from 'react-toastify';
import { MultiSeriesComparisonChart, WaveformChart } from '../respiratory-muscle/RespiratoryMuscleCharts';
import {
  analyzePimaxCsv,
  applyPimaxBreathEdits,
  buildNormalizedPressureComparison,
  buildWavePoints,
  calculatePredictedPimax,
  calculatePimaxLowerLimit,
  normalizeRespiratoryPressureMagnitude,
  sanitizeMetricsForStorage,
  sanitizePlotBreathsForStorage,
  summarizePimaxPayload,
  getPimaxPositionSeries,
  getPimaxPositionLabel,
  getPimaxPrimarySeries,
  RESPIRATORY_POSITION_LABELS,
  toFiniteNumber,
  toLocalDateTimeKey,
  type PimaxAnalysis,
  type PimaxResultPayload,
  type RespiratoryCsvFileMeta,
  type RespiratoryMusclePatientContext,
  type PimaxPositionSeries,
  type PimaxPositionProtocol,
  type RespiratoryTestPosition,
} from '../respiratory-muscle/respiratoryMuscleDomain';

interface PimaxTestProps {
  initial?: (PimaxResultPayload & { chartImages?: Record<string, string> }) | null;
  patientContext?: RespiratoryMusclePatientContext;
  onCancel?: () => void;
  onNext: (result: PimaxResultPayload & { chartImages?: Record<string, string> }) => void;
}

interface SinglePimaxTestProps extends PimaxTestProps {
  positionProtocol: PimaxPositionProtocol;
  onDraftChange: (result: PimaxResultPayload) => void;
  captureRef: MutableRefObject<(() => Promise<PimaxResultPayload>) | null>;
}

export type { PimaxResultPayload };

const parseManualValue = (value: string): number | null => {
  const parsed = Number(value.trim().replace(',', '.'));
  return Number.isFinite(parsed) && parsed !== 0 ? parsed : null;
};

const buildInitialMeasurements = (initial?: PimaxResultPayload | null): string[] => {
  const initialMeasurements = Array.isArray(initial?.rawMeasurements) && initial.rawMeasurements.length
    ? initial.rawMeasurements
    : initial?.measurements;
  if (Array.isArray(initialMeasurements) && initialMeasurements.length) {
    return initialMeasurements.map((value) => String(value).replace('.', ','));
  }
  return ['', '', ''];
};

const buildInitialCsvAnalysis = (initial?: PimaxResultPayload | null): PimaxAnalysis | null => {
  const breathelink = initial?.breathelink;
  if (!breathelink?.metrics?.length || !breathelink.plotBreaths?.length) return null;
  return {
    waveforms: breathelink.plotBreaths,
    metrics: breathelink.metrics,
    plotBreaths: breathelink.plotBreaths,
    maxPressure: toFiniteNumber(initial?.max) ?? breathelink.summary.bestPressure,
    maxSustainedPressure1s: toFiniteNumber(initial?.sustained1s) ?? breathelink.summary.bestSustainedPressure1s,
    quality: breathelink.summary,
  };
};

const exportElement = async (element: HTMLElement): Promise<string> => {
  const html2canvas = (await import('html2canvas')).default;
  const previousWidth = element.style.width;
  const previousMaxWidth = element.style.maxWidth;
  element.style.width = '760px';
  element.style.maxWidth = 'none';
  try {
    await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));
    const canvas = await html2canvas(element, {
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
      useCORS: true,
      allowTaint: true,
      width: 760,
      windowWidth: 900,
    });
    return canvas.toDataURL('image/png');
  } finally {
    element.style.width = previousWidth;
    element.style.maxWidth = previousMaxWidth;
  }
};

const emptyPimaxResult = (): PimaxResultPayload => ({ measurements: [], max: null, predicted: null, percentPredicted: null });
const newPositionSeries = (position: RespiratoryTestPosition): PimaxPositionSeries => ({
  id: crypto.randomUUID(), position, technique: { initialVolume: 'RV', interface: 'mouthpiece' }, result: emptyPimaxResult(),
});

export default function PimaxTest({ initial = null, patientContext, onCancel, onNext }: PimaxTestProps) {
  const [series, setSeries] = useState<PimaxPositionSeries[]>(() => initial ? getPimaxPositionSeries(initial) : [newPositionSeries('seated')]);
  const [activeId, setActiveId] = useState(() => getPimaxPrimarySeries(initial)?.id ?? '');
  const [primaryId, setPrimaryId] = useState(() => getPimaxPrimarySeries(initial)?.id ?? '');
  const active = series.find((item) => item.id === activeId) ?? series[0];
  const draftRef = useRef<PimaxResultPayload | null>(null);
  const captureRef = useRef<(() => Promise<PimaxResultPayload>) | null>(null);
  const [changingSeries, setChangingSeries] = useState(false);
  const onDraftChange = useCallback((result: PimaxResultPayload) => { draftRef.current = result; }, []);
  const context = patientContext ?? initial?.patientContext ?? undefined;
  const positionProtocol = useMemo<PimaxPositionProtocol>(() => ({ ...active.technique,
    position: active.position, angleDegrees: active.angleDegrees, otherPosition: active.otherPosition,
  }), [active.technique, active.position, active.angleDegrees, active.otherPosition]);
  const updateActive = (updates: Partial<PimaxPositionSeries>) => setSeries((items) => items.map((item) => item.id === active.id ? { ...item, ...(draftRef.current ? {result:draftRef.current} : {}), ...updates } : item));
  const flushCurrent = (captured?: PimaxResultPayload): PimaxPositionSeries[] => series.map((item) => item.id === active.id && (captured || draftRef.current)
    ? { ...item, result: captured ?? draftRef.current! } : item);
  const selectSeries = async (id: string) => {
    if (id === active.id || changingSeries) return;
    setChangingSeries(true);
    try { const captured = await captureRef.current?.(); setSeries(flushCurrent(captured)); draftRef.current = null; setActiveId(id); }
    finally { setChangingSeries(false); }
  };
  const addSeries = async () => {
    if (changingSeries) return;
    setChangingSeries(true);
    try { const captured = await captureRef.current?.();
      const item = newPositionSeries(series.some((entry) => entry.position === 'supine') ? 'semi_recumbent' : 'supine');
      setSeries([...flushCurrent(captured), item]); draftRef.current = null; setActiveId(item.id);
    } finally { setChangingSeries(false); }
  };
  const finish = (currentResult?: PimaxResultPayload) => {
    const updated = flushCurrent().map((item) => ({ ...item,
      result: { ...(item.id === active.id && currentResult ? currentResult : item.result), positionSeries: undefined,
        positionProtocol: { ...item.technique, position: item.position, angleDegrees: item.angleDegrees, otherPosition: item.otherPosition } },
    }));
    const measured = updated.filter((item) => !item.notPerformedReason && summarizePimaxPayload(item.result).max != null);
    if (!measured.length) { toast.error('Registre ao menos uma série com medida válida.'); return; }
    if (measured.some((item) => item.position === 'unknown' || (item.position === 'semi_recumbent' && (item.angleDegrees == null || item.angleDegrees < 0 || item.angleDegrees > 90)) || (item.position === 'other' && !item.otherPosition?.trim()))) {
      toast.error('Informe a posição, a inclinação de 0 a 90° quando aplicável e a descrição de outra posição.'); return;
    }
    const incomplete = updated.some((item) => !item.notPerformedReason && summarizePimaxPayload(item.result).max == null);
    if (incomplete) { toast.error('Complete, remova ou registre o motivo de não realização das séries vazias.'); return; }
    const primary = measured.find((item) => item.id === primaryId) ?? measured.find((item) => item.position === 'seated') ?? measured[0];
    onNext({ ...primary.result, positionSeries: updated, primaryPositionSeriesId: primary.id,
      finishedAt: toLocalDateTimeKey(new Date()), patientContext: context });
  };
  return <fieldset disabled={changingSeries} className="min-w-0 space-y-5">
    <section className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex flex-wrap items-center justify-between gap-3"><h3 className="font-bold text-slate-900">Séries por posição</h3><button type="button" onClick={addSeries} className="text-sm font-semibold text-primary-700">Adicionar posição</button></div>
      <p className="text-sm text-slate-600">Cada posição conserva suas medidas e seu CSV. O resumo usa a série principal; a variação sentado/supino é descritiva.</p>
      <div className="flex flex-wrap gap-2">{series.map((item) => <button type="button" key={item.id} onClick={() => selectSeries(item.id)} className={`rounded-lg border px-3 py-2 text-sm ${item.id === active.id ? 'border-primary-500 bg-primary-50 text-primary-800' : 'border-slate-200'}`}>{getPimaxPositionLabel(item)}</button>)}</div>
      <div className="grid gap-3 md:grid-cols-3">
        <label className="text-sm">Posição<select className="mt-1 w-full rounded-lg border p-2" value={active.position} onChange={(e) => updateActive({ position: e.target.value as RespiratoryTestPosition })}>{Object.entries(RESPIRATORY_POSITION_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}</select></label>
        {active.position === 'semi_recumbent' && <label className="text-sm">Inclinação (graus)<input type="number" min="0" max="90" className="mt-1 w-full rounded-lg border p-2" value={active.angleDegrees ?? ''} onChange={(e) => updateActive({ angleDegrees: e.target.value === '' ? null : Number(e.target.value) })}/></label>}
        {active.position === 'other' && <label className="text-sm">Descreva a posição<input className="mt-1 w-full rounded-lg border p-2" value={active.otherPosition ?? ''} onChange={(e) => updateActive({ otherPosition: e.target.value })}/></label>}
        <label className="text-sm">Volume inicial<select className="mt-1 w-full rounded-lg border p-2" value={active.technique.initialVolume} onChange={(e) => updateActive({ technique: { ...active.technique, initialVolume: e.target.value as PimaxPositionProtocol['initialVolume'] } })}><option value="RV">Volume residual (VR)</option><option value="FRC">Capacidade residual funcional (CRF)</option><option value="other">Outro</option><option value="unknown">Não informado</option></select></label>
        <label className="text-sm">Interface<select className="mt-1 w-full rounded-lg border p-2" value={active.technique.interface} onChange={(e) => updateActive({ technique: { ...active.technique, interface: e.target.value as PimaxPositionProtocol['interface'] } })}><option value="mouthpiece">Bocal</option><option value="mask">Máscara</option><option value="other">Outra</option><option value="unknown">Não informada</option></select></label>
        <label className="text-sm">Equipamento<input className="mt-1 w-full rounded-lg border p-2" value={active.technique.device ?? ''} onChange={(e) => updateActive({ technique: { ...active.technique, device: e.target.value } })}/></label>
        <label className="text-sm">Série principal<select className="mt-1 w-full rounded-lg border p-2" value={primaryId || series[0].id} onChange={(e) => setPrimaryId(e.target.value)}>{series.map((item) => <option key={item.id} value={item.id}>{getPimaxPositionLabel(item)}</option>)}</select></label>
      </div>
      <div className="flex flex-wrap gap-4 text-sm">{([['lipLeak','Fuga labial'],['facialSupport','Suporte manual da face'],['cooperationLimited','Cooperação limitada'],['orthopnea','Ortopneia']] as const).map(([key,label]) => <label key={key} className="flex items-center gap-2"><input type="checkbox" checked={Boolean(active.technique[key])} onChange={(e) => updateActive({ technique: { ...active.technique, [key]: e.target.checked } })}/>{label}</label>)}</div>
      <label className="block text-sm">Limitações e contexto técnico<input className="mt-1 w-full rounded-lg border p-2" value={active.technique.notes ?? ''} onChange={(e) => updateActive({ technique: { ...active.technique, notes: e.target.value } })}/></label>
      <label className="block text-sm">Motivo de não realização desta posição (opcional)<input className="mt-1 w-full rounded-lg border p-2" placeholder="Ex.: intolerância ao decúbito / ortopneia" value={active.notPerformedReason ?? ''} onChange={(e) => updateActive({ notPerformedReason: e.target.value.trimStart() || null })}/></label>
      {series.length > 1 && <button type="button" className="text-sm text-rose-700" onClick={() => { const remaining = series.filter((item) => item.id !== active.id); setSeries(remaining); setActiveId(remaining[0].id); draftRef.current = null; }}>Remover esta série</button>}
    </section>
    {changingSeries && <p className="text-sm text-slate-600">Preservando a série e seus gráficos...</p>}
    {active.notPerformedReason ? <div className="space-y-3 rounded-2xl border border-slate-200 p-5"><p className="text-sm text-slate-600">Posição não realizada: {active.notPerformedReason}. Esta série não será usada no cálculo.</p><button type="button" className="rounded-lg bg-primary-600 px-4 py-2 text-white" onClick={() => finish()}>Salvar avaliação</button></div> : <SinglePimaxTest key={active.id} initial={active.result} positionProtocol={positionProtocol} patientContext={context} onCancel={onCancel} onDraftChange={onDraftChange} captureRef={captureRef} onNext={finish}/>}
  </fieldset>;
}

function SinglePimaxTest({ initial = null, patientContext, onCancel, onNext, positionProtocol, onDraftChange, captureRef }: SinglePimaxTestProps) {
  const initialCsvAnalysis = buildInitialCsvAnalysis(initial);
  const [measurements, setMeasurements] = useState<string[]>(buildInitialMeasurements(initial));
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [manualSustained1s, setManualSustained1s] = useState(
    initial?.sustained1s != null ? String(initial.sustained1s).replace('.', ',') : '',
  );
  const [csvAnalysis, setCsvAnalysis] = useState<PimaxAnalysis | null>(initialCsvAnalysis);
  const [originalCsvAnalysis, setOriginalCsvAnalysis] = useState<PimaxAnalysis | null>(initialCsvAnalysis);
  const [selectedBreath, setSelectedBreath] = useState<number | null>(initialCsvAnalysis?.plotBreaths[0]?.breathNumber ?? null);
  const [fileMeta, setFileMeta] = useState<RespiratoryCsvFileMeta | null>(initial?.breathelink?.file ?? null);
  const [csvLoading, setCsvLoading] = useState(false);
  const [csvError, setCsvError] = useState<string | null>(null);
  const [curveView, setCurveView] = useState<'comparison' | 'individual'>('comparison');
  const [comparisonBreaths, setComparisonBreaths] = useState<number[]>(initial?.breathelink?.selectedBreaths ?? []);
  const [breathEdits, setBreathEdits] = useState<Record<number, { trimStart: number; trimEnd: number }>>({});
  const [editingBreath, setEditingBreath] = useState<number | null>(null);
  const [exportingCharts, setExportingCharts] = useState(false);
  const chartRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const ageYears = toFiniteNumber(patientContext?.ageYears);
  const sex = patientContext?.sex ?? null;
  const referenceApplicable = positionProtocol.position === 'seated' && positionProtocol.initialVolume === 'RV' && positionProtocol.interface === 'mouthpiece';
  const predicted = useMemo(
    () => referenceApplicable ? calculatePredictedPimax(ageYears, sex, patientContext?.weightKg) : null,
    [ageYears, patientContext?.weightKg, sex, referenceApplicable],
  );
  const lowerLimit = useMemo(
    () => referenceApplicable ? calculatePimaxLowerLimit(ageYears, sex, patientContext?.weightKg) : null,
    [ageYears, patientContext?.weightKg, sex, referenceApplicable],
  );
  const rawMeasurementValues = useMemo(
    () => measurements.map(parseManualValue).filter((value): value is number => value != null),
    [measurements],
  );
  const measurementValues = useMemo(
    () => rawMeasurementValues
      .map(normalizeRespiratoryPressureMagnitude)
      .filter((value): value is number => value != null),
    [rawMeasurementValues],
  );
  const manualMax = measurementValues.length ? Math.max(...measurementValues) : null;
  const effectiveMax = csvAnalysis ? csvAnalysis.maxPressure : manualMax;
  const rawManualSustained1s = parseManualValue(manualSustained1s);
  const effectiveSustained1s = csvAnalysis
    ? csvAnalysis.maxSustainedPressure1s
    : normalizeRespiratoryPressureMagnitude(rawManualSustained1s);
  const percentPredicted =
    predicted != null && predicted > 0 && effectiveMax != null ? Number(((effectiveMax / predicted) * 100).toFixed(1)) : null;
  const canFinish = csvAnalysis ? csvAnalysis.metrics.length >= 1 : measurementValues.length >= 1 && manualMax != null;

  const availableBreathNumbers = useMemo(() => csvAnalysis?.plotBreaths.map((breath) => breath.breathNumber) ?? [], [csvAnalysis]);
  const defaultComparisonBreaths = useMemo(() => {
    if (!csvAnalysis) return [];
    return csvAnalysis.quality.bestBreathNumbers.length
      ? csvAnalysis.quality.bestBreathNumbers
      : csvAnalysis.plotBreaths.slice(0, 3).map((breath) => breath.breathNumber);
  }, [csvAnalysis]);
  const comparisonBreathNumbers = comparisonBreaths.length ? comparisonBreaths : defaultComparisonBreaths;

  const selectedWaveform = useMemo(() => {
    if (!csvAnalysis) return null;
    if (selectedBreath == null) return csvAnalysis.plotBreaths[0] ?? null;
    return csvAnalysis.plotBreaths.find((plot) => plot.breathNumber === selectedBreath) ?? csvAnalysis.plotBreaths[0] ?? null;
  }, [csvAnalysis, selectedBreath]);
  const chartData = useMemo(() => buildWavePoints(selectedWaveform), [selectedWaveform]);
  const normalizedCurves = useMemo(
    () => (csvAnalysis ? buildNormalizedPressureComparison(csvAnalysis.plotBreaths, comparisonBreathNumbers) : null),
    [comparisonBreathNumbers, csvAnalysis],
  );

  const previewPayload: PimaxResultPayload = useMemo(
    () => ({
      report: initial?.report,
      finishedAt: initial?.finishedAt,
      measurements: csvAnalysis ? csvAnalysis.metrics.map((metric) => Number(metric.peakPressure.toFixed(2))) : measurementValues,
      rawMeasurements: csvAnalysis ? undefined : rawMeasurementValues,
      max: effectiveMax,
      sustained1s: effectiveSustained1s,
      rawSustained1s: csvAnalysis ? undefined : rawManualSustained1s,
      predicted,
      percentPredicted,
      lowerLimit,
      belowLowerLimit: lowerLimit != null && effectiveMax != null ? effectiveMax < lowerLimit : null,
      notes: notes.trim() || null,
      breathelink: csvAnalysis
        ? {
            summary: csvAnalysis.quality,
            metrics: sanitizeMetricsForStorage(csvAnalysis.metrics),
            plotBreaths: sanitizePlotBreathsForStorage(csvAnalysis.plotBreaths),
            selectedBreaths: comparisonBreathNumbers,
            ...(fileMeta ? { file: fileMeta } : {}),
          }
        : null,
      patientContext,
      positionProtocol,
    }),
    [comparisonBreathNumbers, csvAnalysis, effectiveMax, effectiveSustained1s, fileMeta, lowerLimit, measurementValues, notes, patientContext, percentPredicted, predicted, rawManualSustained1s, rawMeasurementValues, positionProtocol, initial?.report, initial?.finishedAt],
  );
  useEffect(() => { onDraftChange(previewPayload); }, [onDraftChange, previewPayload]);
  const interpretation = summarizePimaxPayload(previewPayload).interpretation;

  useEffect(() => {
    if (!csvAnalysis?.plotBreaths.length) {
      setEditingBreath(null);
      return;
    }
    const exists = editingBreath != null && csvAnalysis.plotBreaths.some((breath) => breath.breathNumber === editingBreath);
    if (!exists) setEditingBreath(csvAnalysis.plotBreaths[0].breathNumber);
  }, [csvAnalysis, editingBreath]);

  useEffect(() => {
    if (!originalCsvAnalysis) return;
    setCsvAnalysis(Object.keys(breathEdits).length ? applyPimaxBreathEdits(originalCsvAnalysis, breathEdits) : originalCsvAnalysis);
  }, [breathEdits, originalCsvAnalysis]);

  const updateMeasurement = (index: number, value: string) => {
    setMeasurements((current) => {
      const next = [...current];
      next[index] = value;
      return next;
    });
  };

  const clearCsv = () => {
    setCsvAnalysis(null);
    setOriginalCsvAnalysis(null);
    setSelectedBreath(null);
    setFileMeta(null);
    setCsvError(null);
    setComparisonBreaths([]);
    setBreathEdits({});
    setEditingBreath(null);
    setCurveView('comparison');
  };

  const handleCsvFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setCsvLoading(true);
    setCsvError(null);
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = analyzePimaxCsv(typeof reader.result === 'string' ? reader.result : '');
        const initialSelection = parsed.quality.bestBreathNumbers.length
          ? parsed.quality.bestBreathNumbers
          : parsed.plotBreaths.slice(0, 3).map((breath) => breath.breathNumber);
        setCsvAnalysis(parsed);
        setOriginalCsvAnalysis(parsed);
        setSelectedBreath(parsed.plotBreaths[0]?.breathNumber ?? null);
        setComparisonBreaths(initialSelection);
        setBreathEdits({});
        setEditingBreath(parsed.plotBreaths[0]?.breathNumber ?? null);
        setFileMeta({ name: file.name, size: file.size, importedAt: toLocalDateTimeKey(new Date()) });
      } catch (error) {
        console.error('Erro ao processar PImax:', error);
        clearCsv();
        setCsvError(error instanceof Error ? error.message : 'Não foi possível processar o arquivo.');
      } finally {
        setCsvLoading(false);
        event.target.value = '';
      }
    };
    reader.onerror = () => {
      setCsvLoading(false);
      setCsvError('Erro ao ler o arquivo.');
      event.target.value = '';
    };
    reader.readAsText(file, 'utf-8');
  };

  const toggleComparisonBreath = (breathNumber: number) => {
    setComparisonBreaths((current) => {
      const base = current.length ? current : defaultComparisonBreaths;
      if (base.includes(breathNumber)) return base.length <= 1 ? base : base.filter((value) => value !== breathNumber);
      return [...base, breathNumber];
    });
  };

  const updateBreathEdit = (breathNumber: number, updates: Partial<{ trimStart: number; trimEnd: number }>) => {
    setBreathEdits((current) => {
      const nextEdit = { ...(current[breathNumber] || { trimStart: 0, trimEnd: 0 }), ...updates };
      return { ...current, [breathNumber]: nextEdit };
    });
  };

  const resetBreathEdit = (breathNumber: number) => {
    setBreathEdits((current) => {
      const next = { ...current };
      delete next[breathNumber];
      return next;
    });
  };

  const exportChartsAsImages = async (): Promise<Record<string, string>> => {
    if (!csvAnalysis) return {};
    setExportingCharts(true);
    try {
      const images: Record<string, string> = {};
      for (const key of ['comparison', 'individual']) {
        const element = chartRefs.current[`export-${key}`] ?? chartRefs.current[key];
        if (!element) continue;
        images[key] = await exportElement(element);
      }
      return images;
    } catch (error) {
      console.error('Erro ao exportar gráficos PImax:', error);
      return {};
    } finally {
      setExportingCharts(false);
    }
  };

  useEffect(() => {
    captureRef.current = async () => ({ ...previewPayload, chartImages: await exportChartsAsImages() });
    return () => { captureRef.current = null; };
  });

  const handleFinish = async () => {
    if (!csvAnalysis && measurements.some((value) => value.trim() && parseManualValue(value) == null)) {
      toast.error('Corrija as medidas inválidas; use pressões numéricas diferentes de zero.');
      return;
    }
    if (!canFinish || effectiveMax == null) {
      toast.error('Registre ao menos uma medida válida. Recomenda-se obter três manobras reprodutíveis.');
      return;
    }
    const chartImages = await exportChartsAsImages();
    onNext({
      ...previewPayload,
      ...(Object.keys(chartImages).length ? { chartImages } : {}),
      finishedAt: toLocalDateTimeKey(new Date()),
    });
  };

  const patientSummary = [
    ageYears != null ? `${ageYears} anos` : null,
    sex ? sex : null,
    patientContext?.weightKg != null ? `${patientContext.weightKg} kg` : null,
    patientContext?.heightCm != null ? `${patientContext.heightCm} cm` : null,
  ].filter(Boolean).join(' - ') || 'Dados do paciente incompletos.';

  return (
    <div className="space-y-4">
      <div className="flex items-start gap-2 rounded-md bg-blue-50 p-3 text-blue-900">
        <Info className="mt-0.5 h-5 w-5" />
        <div>
          <p className="font-medium">PImax — {getPimaxPositionLabel(positionProtocol)}</p>
          <p className="text-sm opacity-90">Recomenda-se obter ao menos 3 manobras válidas ou importar um CSV do BreatheLink (TEST/MIP). Resultados parciais podem ser salvos com ressalva de qualidade.</p>
        </div>
      </div>

      <div className="rounded-lg border border-dashed p-4 text-sm text-slate-700">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 font-medium">
            <FileUp className="h-4 w-4" />
            Importar CSV do BreatheLink
          </div>
          <div className="flex items-center gap-2">
            {csvAnalysis && (
              <button type="button" className="rounded border px-3 py-1 text-xs font-medium text-slate-700 hover:bg-white" onClick={clearCsv}>
                Remover arquivo
              </button>
            )}
            <label className="cursor-pointer rounded bg-blue-600 px-3 py-1 text-xs font-semibold text-white hover:bg-blue-700">
              Selecionar CSV
              <input type="file" accept=".csv" className="hidden" onChange={handleCsvFileChange} />
            </label>
          </div>
        </div>
        <div className="mt-2 text-xs text-slate-500">Critério: {'>='} 3 curvas e variação {'<='} 5% entre os 2 maiores valores.</div>
        {fileMeta && <div className="mt-2 text-xs text-emerald-700">Arquivo carregado: {fileMeta.name} ({Math.round(fileMeta.size / 1024)} KB)</div>}
        {csvLoading && <div className="mt-2 text-xs text-blue-600">Processando arquivo...</div>}
        {csvError && <div className="mt-2 text-xs text-red-600">{csvError}</div>}
      </div>

      <div className="rounded-lg border p-4 text-sm text-slate-700">
        <div className="text-xs uppercase tracking-wide text-slate-500">Dados do paciente</div>
        <div className="mt-1">{patientSummary}</div>
      </div>

      {!csvAnalysis ? (
        <div className="space-y-4 rounded-lg border p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Medições de PImax (cmH2O)</h3>
            <button type="button" onClick={() => setMeasurements((current) => [...current, ''])} className="inline-flex items-center gap-2 text-sm text-blue-600">
              <Plus className="h-4 w-4" />
              Adicionar medida
            </button>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            {measurements.map((value, index) => (
              <label key={`pimax-measure-${index}`} className="text-sm">
                {index + 1}a medida
                <div className="mt-1 flex items-center gap-2">
                  <input className="w-full rounded border px-2 py-1" inputMode="decimal" value={value} onChange={(event) => updateMeasurement(index, event.target.value)} />
                  {measurements.length > 3 && (
                    <button type="button" onClick={() => setMeasurements((current) => current.filter((_, itemIndex) => itemIndex !== index))} className="p-2 text-slate-500 hover:text-red-600" aria-label="Remover medida">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </label>
            ))}
          </div>
          <div className="text-xs text-slate-500">
            Valores negativos são aceitos como convenção de sinal e comparados pela magnitude; o valor digitado também é preservado no registro.
          </div>
          <label className="block max-w-sm text-sm">
            PImax sustentada por 1 segundo (opcional)
            <input
              className="mt-1 w-full rounded border px-2 py-1"
              inputMode="decimal"
              value={manualSustained1s}
              onChange={(event) => setManualSustained1s(event.target.value)}
              placeholder="Ex.: 82,5"
            />
          </label>
        </div>
      ) : (
        <div className="space-y-4 rounded-lg border p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h3 className="font-semibold">Qualidade do teste via CSV</h3>
            <div className={`rounded px-2 py-1 text-xs font-semibold ${csvAnalysis.quality.meetsCriteria ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
              {csvAnalysis.quality.meetsCriteria ? 'REPRODUTÍVEL' : 'REPETIÇÃO RECOMENDADA'}
            </div>
          </div>

          <div className="grid gap-3 text-sm md:grid-cols-4">
            <Metric label="Curvas válidas" value={String(csvAnalysis.quality.maneuversAnalyzed)} />
            <Metric label="Melhor PImax" value={`${csvAnalysis.quality.bestPressure.toFixed(1)} cmH2O`} />
            <Metric label="2º melhor" value={csvAnalysis.quality.secondBestPressure != null ? `${csvAnalysis.quality.secondBestPressure.toFixed(1)} cmH2O` : '-'} />
            <Metric label="Variação entre as 2 melhores curvas" value={csvAnalysis.quality.variationTop2Percent != null ? `${csvAnalysis.quality.variationTop2Percent.toFixed(2)}%` : '-'} />
          </div>

          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <button type="button" className={`rounded border px-3 py-1 text-sm ${curveView === 'comparison' ? 'border-blue-600 bg-blue-600 text-white' : 'bg-white'}`} onClick={() => setCurveView('comparison')}>
                Comparar curvas
              </button>
              <button type="button" className={`rounded border px-3 py-1 text-sm ${curveView === 'individual' ? 'border-blue-600 bg-blue-600 text-white' : 'bg-white'}`} onClick={() => setCurveView('individual')}>
                Curva individual
              </button>
            </div>

            {editingBreath != null && (
              <CurveEditPanel
                breathNumber={editingBreath}
                breathNumbers={availableBreathNumbers}
                edit={breathEdits[editingBreath]}
                onSelect={setEditingBreath}
                onUpdate={(updates) => updateBreathEdit(editingBreath, updates)}
                onReset={() => resetBreathEdit(editingBreath)}
                onResetAll={() => setBreathEdits({})}
              />
            )}

            {curveView === 'comparison' && normalizedCurves && (
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <button type="button" className="rounded border px-2 py-1 hover:bg-slate-50" onClick={() => setComparisonBreaths(defaultComparisonBreaths)}>
                    Melhores
                  </button>
                  <button type="button" className="rounded border px-2 py-1 hover:bg-slate-50" onClick={() => setComparisonBreaths(availableBreathNumbers)}>
                    Todas
                  </button>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  {availableBreathNumbers.map((breathNumber) => (
                    <label key={breathNumber} className={`inline-flex cursor-pointer items-center gap-1 rounded border px-2 py-1 ${comparisonBreathNumbers.includes(breathNumber) ? 'border-blue-200 bg-blue-50 text-blue-800' : 'bg-white text-slate-700'}`}>
                      <input type="checkbox" className="accent-blue-600" checked={comparisonBreathNumbers.includes(breathNumber)} onChange={() => toggleComparisonBreath(breathNumber)} />
                      Curva {breathNumber}
                    </label>
                  ))}
                </div>
                <div ref={(element) => (chartRefs.current.comparison = element)}>
                  <MultiSeriesComparisonChart title="Curvas de pressão normalizadas" unit="cmH2O" data={normalizedCurves.data} seriesMeta={normalizedCurves.seriesMeta} />
                </div>
              </div>
            )}

            {curveView === 'individual' && (
              <div className="space-y-3">
                <select className="rounded border px-2 py-1 text-sm" value={selectedWaveform?.breathNumber ?? ''} onChange={(event) => setSelectedBreath(Number(event.target.value))}>
                  {csvAnalysis.plotBreaths.map((breath) => (
                    <option key={breath.breathNumber} value={breath.breathNumber}>
                      Curva {breath.breathNumber}
                    </option>
                  ))}
                </select>
                <div ref={(element) => (chartRefs.current.individual = element)}>
                  <WaveformChart title="Pressão (cmH2O)" color="#2563eb" unit="cmH2O" dataKey="pressure" data={chartData} />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className={`grid gap-4 ${effectiveSustained1s != null ? 'md:grid-cols-4' : 'md:grid-cols-3'}`}>
        <Metric label="PImax pico" value={effectiveMax != null ? `${effectiveMax.toFixed(0)} cmH2O` : '-'} emphasis />
        {effectiveSustained1s != null && <Metric label="PImax sustentada (1 s)" value={`${effectiveSustained1s.toFixed(1)} cmH2O`} />}
        {predicted != null && <Metric label="Predito (Neder)" value={`${predicted.toFixed(1)} cmH2O`} />}
        {percentPredicted != null && <Metric label="% do predito" value={`${percentPredicted.toFixed(1)}%`} />}
      </div>
      {!referenceApplicable && <p className="text-sm text-slate-600">Comparação normativa não aplicada: a referência existente requer protocolo compatível sentado, a partir do volume residual e com bocal. Esta série será descrita pela magnitude e pelo contexto técnico.</p>}

      <div className={`rounded-lg border p-3 text-sm ${interpretation.tone === 'good' ? 'border-emerald-200 bg-emerald-50 text-emerald-900' : interpretation.tone === 'alert' ? 'border-rose-200 bg-rose-50 text-rose-900' : interpretation.tone === 'warn' ? 'border-amber-200 bg-amber-50 text-amber-900' : 'border-slate-200 bg-slate-50 text-slate-900'}`}>
        <div className="font-semibold">{interpretation.title}</div>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
          {interpretation.lines.map((line, index) => <li key={`pimax-interpretation-${index}`}>{line}</li>)}
        </ul>
      </div>

      <label className="block text-sm">
        Observações
        <textarea className="mt-1 w-full rounded border px-2 py-1" rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} />
      </label>

      {exportingCharts && <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">Exportando gráficos para impressão...</div>}

      {csvAnalysis && <div aria-hidden="true" className="pointer-events-none fixed top-0" style={{left:-10000,width:760}}>
        {normalizedCurves && <div ref={(element) => (chartRefs.current['export-comparison'] = element)}><MultiSeriesComparisonChart title="Curvas de pressão normalizadas" unit="cmH2O" data={normalizedCurves.data} seriesMeta={normalizedCurves.seriesMeta}/></div>}
        {selectedWaveform && <div ref={(element) => (chartRefs.current['export-individual'] = element)}><WaveformChart title="Pressão (cmH2O)" color="#2563eb" unit="cmH2O" dataKey="pressure" data={chartData}/></div>}
      </div>}

      <div className="flex items-center justify-between pt-2">
        <button type="button" className="text-sm text-slate-600 hover:text-slate-800" onClick={onCancel}>
          Cancelar
        </button>
        <button type="button" className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-white disabled:opacity-50" disabled={!canFinish} onClick={handleFinish}>
          {initial?.finishedAt ? 'Salvar alterações' : 'Salvar avaliação de PImax'} <Check className="ml-1 h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

const Metric = ({ label, value, emphasis = false }: { label: string; value: string; emphasis?: boolean }) => (
  <div className="rounded-lg border bg-white p-3 shadow-sm">
    <div className="text-xs uppercase text-slate-500">{label}</div>
    <div className={`text-2xl font-extrabold ${emphasis ? 'text-blue-700' : 'text-slate-900'}`}>{value}</div>
  </div>
);

const CurveEditPanel = ({
  breathNumber,
  breathNumbers,
  edit,
  onSelect,
  onUpdate,
  onReset,
  onResetAll,
}: {
  breathNumber: number;
  breathNumbers: number[];
  edit?: { trimStart: number; trimEnd: number };
  onSelect: (breathNumber: number) => void;
  onUpdate: (updates: Partial<{ trimStart: number; trimEnd: number }>) => void;
  onReset: () => void;
  onResetAll: () => void;
}) => {
  const trimStart = edit?.trimStart ?? 0;
  const trimEnd = edit?.trimEnd ?? 0;
  const buildSafeValue = (value: number, other: number) => {
    const safe = Number.isFinite(value) ? Math.min(90, Math.max(0, value)) : 0;
    return safe + other > 90 ? Math.max(0, 90 - other) : safe;
  };

  return (
    <div className="space-y-2 rounded-lg border bg-white p-3 shadow-sm">
      <div className="text-xs uppercase text-slate-500">Editar curva especifica</div>
      <div className="flex flex-wrap items-end gap-3 text-sm">
        <label>
          Curva
          <select className="mt-1 rounded border px-2 py-1" value={breathNumber} onChange={(event) => onSelect(Number(event.target.value))}>
            {breathNumbers.map((item) => <option key={item} value={item}>Curva {item}</option>)}
          </select>
        </label>
        <label>
          Cortar inicio (%)
          <input className="mt-1 w-24 rounded border px-2 py-1" type="number" min={0} max={90} value={trimStart} onChange={(event) => onUpdate({ trimStart: buildSafeValue(Number(event.target.value), trimEnd) })} />
        </label>
        <label>
          Cortar final (%)
          <input className="mt-1 w-24 rounded border px-2 py-1" type="number" min={0} max={90} value={trimEnd} onChange={(event) => onUpdate({ trimEnd: buildSafeValue(Number(event.target.value), trimStart) })} />
        </label>
        <button type="button" className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={onReset}>Resetar curva</button>
        <button type="button" className="rounded border px-2 py-1 text-xs hover:bg-slate-50" onClick={onResetAll}>Resetar tudo</button>
      </div>
      <div className="text-xs text-slate-500">O corte recalcula o pico e o PImax sustentado em 1s antes de salvar.</div>
    </div>
  );
};
